import {ReactNode} from 'react';
import {useIsMobile} from '@legit-health/ui';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {Box, Button, MobileStepper} from '@mui/material';
import {TFunction} from 'i18next';

const mobileStepperProps = {
  position: 'bottom',
  variant: 'progress',
  linearProgressProps: {
    sx: {
      bgcolor: '#eae8f7',
      '& .MuiLinearProgress-bar1Determinate': {
        backgroundImage:
          'linear-gradient( 95deg,rgb(134, 113, 255) 0%,rgb(134, 113, 255) 33%,rgb(4, 175, 101) 100%)'
      }
    }
  }
} as const;

export default function BottomBar({
  activeStep,
  isBackEnabled = true,
  isBackVisible = true,
  isNextEnabled,
  isNextVisible = true,
  nextButtonType = 'button',
  onBack,
  onNext,
  rightButton = null,
  t,
  totalSteps
}: {
  activeStep: number;
  isBackEnabled?: boolean;
  isBackVisible?: boolean;
  isNextEnabled?: boolean;
  isNextVisible?: boolean;
  nextButtonType?: 'reset' | 'submit' | 'button';
  onBack?: () => void;
  onNext?: () => void;
  rightButton?: ReactNode;
  t: TFunction;
  totalSteps: number;
}) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <MobileStepper
        variant={mobileStepperProps.variant}
        steps={totalSteps}
        position={mobileStepperProps.position}
        LinearProgressProps={mobileStepperProps.linearProgressProps}
        activeStep={activeStep}
        sx={(theme) => ({
          '&.MuiMobileStepper-positionBottom': {
            pt: 2,
            pb: `max(env(safe-area-inset-bottom, ${theme.spacing(2)}), ${theme.spacing(2)})`
          }
        })}
        backButton={
          <Button
            data-testid="stepperBackButton"
            disabled={!isBackEnabled}
            size="medium"
            onClick={onBack}
            disableElevation
          >
            <ArrowBackIosIcon />
            {t('ui:back')}
          </Button>
        }
        nextButton={
          rightButton ?? (
            <Button
              data-testid="stepperNextButton"
              size="medium"
              onClick={onNext}
              disableElevation
              disabled={!isNextEnabled}
              type={nextButtonType}
              sx={{
                visibility: isNextVisible ? 'visible' : 'hidden'
              }}
            >
              {t('ui:next')}
              <ArrowForwardIosIcon />
            </Button>
          )
        }
      />
    );
  }
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        width: '100%',
        left: 0,
        display: 'flex',
        justifyContent: 'center',
        background: '#fff',
        boxShadow: 20
      }}
    >
      <Box sx={{display: 'flex', py: 1, columnGap: 2, alignItems: 'center'}}>
        <Button
          disabled={!isBackEnabled}
          onClick={onBack}
          disableElevation
          size="medium"
          variant="outlined"
          startIcon={<ArrowBackIosIcon />}
          sx={{
            bgcolor: 'transparent',
            borderColor: 'transparent',
            color: 'grey.500',
            visibility: isBackVisible ? 'visible' : 'hidden',
            '&.Mui-disabled': {
              bgcolor: 'transparent',
              borderColor: 'transparent'
            },
            '&:hover': {
              bgcolor: 'transparent',
              borderColor: 'transparent'
            }
          }}
        >
          {t('ui:back')}
        </Button>
        {rightButton ?? (
          <Button
            color="secondary"
            onClick={onNext}
            disableElevation
            size="medium"
            variant="contained"
            endIcon={<ArrowForwardIosIcon />}
            data-testid="stepperNextButton"
            disabled={!isNextEnabled}
            type={nextButtonType}
            sx={{
              visibility: isNextVisible ? 'visible' : 'hidden'
            }}
          >
            {t('ui:next')}
          </Button>
        )}
      </Box>
    </Box>
  );
}
